import { getDefaultClient, type IClient } from "./Client.js";
import type { DataObject } from "./DataObject.js";
import { EventTarget } from "./events.js";
import { UploadManager, type UploadManagerOptions } from "./upload/UploadManager.js";

export type FileUploaderOptions =
	| {
			articleId?: string;
			dataObject: DataObject<any>;
			fieldName?: string;
			primKey?: string;
			route?: undefined;
			client?: IClient | null;
	  }
	| {
			articleId?: undefined;
			dataObject?: undefined;
			fieldName?: undefined;
			primKey?: undefined;
			route: string;
			client?: IClient | null;
	  }
	| {
			articleId: string;
			dataObject: string;
			fieldName?: string;
			primKey?: string;
			client?: IClient | null;
			route?: undefined;
	  };

export type FileUploaderEventToValueMap = {
	onBeforeUpload: null;
	onUploaded: null;
	onUploadFailed: { error: unknown };
	onProgress: {
		loaded: number;
		total: number;
	};
};

export type FileUploaderEvent = keyof FileUploaderEventToValueMap;

export const FileUploaderEvents: Record<FileUploaderEvent, { abortable: boolean }> = {
	onBeforeUpload: { abortable: true },
	onUploaded: { abortable: false },
	onUploadFailed: { abortable: false },
	onProgress: { abortable: false },
};

/**
 * @deprecated Use `UploadManager` and `UploadTask` instead.
 */
export class FileUploader extends EventTarget<FileUploaderEventToValueMap> {
	protected dataObject: DataObject<any> | string | undefined;
	protected fieldName: string | undefined;
	protected options: FileUploaderOptions;
	protected managerOptions: UploadManagerOptions;
	protected manager: UploadManager;

	get client(): IClient {
		return this.options.client ?? getDefaultClient();
	}
	set client(value: IClient | null) {
		this.options.client = value;
		this.managerOptions.client = value;
		this.manager = new UploadManager(this.managerOptions);
	}

	constructor(options: FileUploaderOptions) {
		super(FileUploaderEvents);
		this.options = { ...options };
		this.dataObject = options.dataObject;
		this.fieldName = options.fieldName;
		this.managerOptions = {
			client: options.client,
			url: options.route,
			dataHandler: typeof options.dataObject === "string" ? undefined : options.dataObject?.dataHandler,
			fieldName: options.fieldName,
			primKey: options.primKey,
			concurrency: 1,
		};
		this.manager = new UploadManager(this.managerOptions);
	}

	getUploadUrl(): string {
		if (this.managerOptions.url) {
			return this.managerOptions.url;
		}
		if (typeof this.dataObject !== "string") {
			return (
				this.dataObject?.dataHandler?.getUploadUrl({
					fieldName: this.fieldName,
					primKey: this.managerOptions.primKey,
				}) ?? ""
			);
		}
		let articleId = this.options.articleId ?? globalThis.af?.article?.id;
		let url = `/file/upload/${articleId}/${this.dataObject}`;
		if (this.fieldName) {
			url += `/${this.fieldName}`;
		}
		if (this.managerOptions.primKey) {
			url += `/${this.managerOptions.primKey}`;
		}
		return url;
	}

	async upload(fileOrBlob: File | Blob, fields?: Record<string, any>, fileName?: string): Promise<unknown> {
		this.fireEvent("onBeforeUpload", null);

		const masterCriteria =
			typeof this.dataObject === "string"
				? {}
				: (this.dataObject?.getParameter("masterChildCriteria") as Record<string, string | Blob>) || {};

		if (fields?.["PrimKey"]) {
			this.options.primKey = fields.PrimKey;
			this.managerOptions.primKey = fields.PrimKey;
			this.managerOptions.url = undefined;
			delete fields.PrimKey;
		}

		if (!this.managerOptions.dataHandler) {
			this.managerOptions.url = this.getUploadUrl();
		}

		const allFields = {
			...masterCriteria,
			...(this.managerOptions.primKey ? { PrimKey: this.managerOptions.primKey } : {}),
			...(fields ?? {}),
		} as Record<string, any>;

		const task = this.manager.upload(fileOrBlob, allFields, fileName);
		const handleProgress = (evt: CustomEvent<{ loaded: number; total: number }>) =>
			this.fireEvent("onProgress", evt.detail);
		task.attachEvent("onProgress", handleProgress);

		try {
			const result = await task.start();
			this.fireEvent("onUploaded", null);
			return result;
		} catch (error) {
			this.fireEvent("onUploadFailed", { error });
			throw error;
		} finally {
			task.detachEvent("onProgress", handleProgress);
		}
	}
}
